import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import PublicLayout from "@/components/public/PublicLayout";
import HomePage from "@/pages/public/HomePage";
import FeaturesPage from "@/pages/public/FeaturesPage";
import SolutionsPage from "@/pages/public/SolutionsPage";
import AboutPage from "@/pages/public/AboutPage";
import ContactPage from "@/pages/public/ContactPage";
import RequestDemoPage from "@/pages/public/RequestDemoPage";
import PrivacyPolicyPage from "@/pages/public/PrivacyPolicyPage";
import TermsPage from "@/pages/public/TermsPage";
import PricingPage from "@/pages/public/PricingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import JoinPage from "@/pages/JoinPage";
import TeamPage from "@/pages/portal/TeamPage";
import RequestsPage from "@/pages/portal/RequestsPage";
import DayReviewPage from "@/pages/portal/DayReviewPage";
import TripDetailPage from "@/pages/portal/TripDetailPage";
import TimeFixesPage from "@/pages/portal/TimeFixesPage";
import InspectionsPage from "@/pages/portal/InspectionsPage";
import TicketLookupPage from "@/pages/portal/TicketLookupPage";
import TripHistoryPage from "@/pages/portal/TripHistoryPage";

import PortalLayout from "@/components/portal/PortalLayout";
import DashboardPage from "@/pages/portal/DashboardPage";
import DispatchPage from "@/pages/portal/DispatchPage";
import FleetPage from "@/pages/portal/FleetPage";
import DriverDetailPage from "@/pages/portal/DriverDetailPage";
import ImportPage from "@/pages/portal/ImportPage";
import StandingOrdersPage from "@/pages/portal/StandingOrdersPage";
import IncidentsPage from "@/pages/portal/IncidentsPage";
import ReportsPage from "@/pages/portal/ReportsPage";
import SettingsPage from "@/pages/portal/SettingsPage";
import NotificationsPage from "@/pages/portal/NotificationsPage";
import BillingPage from "@/pages/portal/BillingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import FounderLogin from "@/pages/founder/FounderLogin";
import FounderLayout from "@/pages/founder/FounderLayout";
import FounderOverview from "@/pages/founder/FounderOverview";
import FounderCompanies from "@/pages/founder/FounderCompanies";
import FounderCompany from "@/pages/founder/FounderCompany";
import FounderRevenue from "@/pages/founder/FounderRevenue";
import FounderGrowth from "@/pages/founder/FounderGrowth";
import UpgradeGate from "@/components/portal/UpgradeGate";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/solutions" element={<SolutionsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/request-demo" element={<RequestDemoPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Route>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/founder" element={<FounderLogin />} />
            <Route path="/founder" element={<FounderLayout />}>
              <Route path="overview" element={<FounderOverview />} />
              <Route path="companies" element={<FounderCompanies />} />
              <Route path="companies/:orgId" element={<FounderCompany />} />
              <Route path="revenue" element={<FounderRevenue />} />
              <Route path="growth" element={<FounderGrowth />} />
            </Route>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />

            <Route
              path="/portal"
              element={
                <ProtectedRoute>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="dispatch" element={<DispatchPage />} />
              <Route path="standing-orders" element={<StandingOrdersPage />} />
              <Route path="fleet" element={<FleetPage />} />
              <Route path="drivers/:id" element={<DriverDetailPage />} />
              <Route path="trips/:id" element={<TripDetailPage />} />
              <Route
                path="inspections"
                element={
                  <ProtectedRoute roles={["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"]}>
                    <InspectionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="ticket-lookup"
                element={
                  <ProtectedRoute roles={["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"]}>
                    <TicketLookupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="time-fixes"
                element={
                  <ProtectedRoute roles={["owner", "supervisor", "dispatcher"]}>
                    <TimeFixesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="import"
                element={
                  <ProtectedRoute roles={["owner", "supervisor", "dispatcher", "fleet_manager"]}>
                    <UpgradeGate feature="import" title="CSV / broker trip import is a Fleet feature">
                      <ImportPage />
                    </UpgradeGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="incidents"
                element={
                  <ProtectedRoute roles={["owner", "supervisor", "administrator", "fleet_manager"]}>
                    <UpgradeGate feature="incidents" title="Incident management is a Growth feature">
                      <IncidentsPage />
                    </UpgradeGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute roles={["owner", "supervisor", "administrator", "fleet_manager"]}>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute roles={["owner", "administrator"]}>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="history"
                element={
                  <ProtectedRoute roles={["owner"]}>
                    <TripHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="day-review"
                element={
                  <ProtectedRoute roles={["owner"]}>
                    <DayReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="requests"
                element={
                  <ProtectedRoute roles={["owner"]}>
                    <RequestsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="team"
                element={
                  <ProtectedRoute roles={["owner", "administrator"]}>
                    <TeamPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="billing"
                element={
                  <ProtectedRoute roles={["owner", "administrator"]}>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
